import React, { useState, useEffect } from 'react';
import { CompleteKundali, Language, ChartStyle } from '../../types/kundali';
import { UserProfile } from '../../types/user';
import { AdvancedLifeAnalysisResult, LifeThemeId, LifeThemeQuestion } from '../../types/advancedLifeAnalysis';
import { LIFE_THEMES_REGISTRY } from '../../utils/advancedLifeAnalysisTaxonomy';
import { getNatalPromiseTier, getTimingConfluence, calculateProjectedAgeWindow } from '../../utils/presentationHelper';
import { formatDOB, formatTimeToAMPM, calculateAgeString } from '../../utils/dateUtils';

// Subcomponents
import { LifeStageContextBanner } from '../LifeStageContextBanner';
import { NatalPromiseVerdictCard } from '../NatalPromiseVerdictCard';
import { AdvancedChartRail } from '../AdvancedChartRail';
import { AstrologicalEvidenceGrid } from '../AstrologicalEvidenceGrid';
import { DashaGocharConvergenceCard } from '../DashaGocharConvergenceCard';
import { PhalaBhutaTimeline } from '../PhalaBhutaTimeline';
import { AcharyaSanctuaryContainer } from '../AcharyaSanctuaryContainer';
import { RemediesGuidanceCard } from '../RemediesGuidanceCard';

import {
  Award,
  Clock,
  Play,
  Pause,
  RotateCcw,
  Save,
  Check,
  FileText,
  Sparkles,
  Zap,
  Printer,
  Compass,
  HelpCircle,
  CheckCircle2,
  Calendar,
  Layers,
  User,
  Shield,
  Briefcase,
  Heart,
  Coins,
  Home,
  Car,
  Baby,
  GraduationCap,
  Activity,
  TrendingUp,
  Globe,
  Scale,
  Users,
  Copy,
  Plus,
  Edit3
} from 'lucide-react';

interface AcharyaConsultationViewProps {
  kundali: CompleteKundali;
  analysisResult: AdvancedLifeAnalysisResult;
  selectedThemeId: LifeThemeId;
  onSelectTheme: (id: LifeThemeId) => void;
  selectedQuestionId: string;
  onSelectQuestion: (id: string) => void;
  lang: Language;
  chartStyle: ChartStyle;
  setChartStyle: (style: ChartStyle) => void;
  onOpenPrintReport: (mode?: 'BASIC' | 'LEARNER' | 'PRO' | 'ACHARYA') => void;
  onEditKundali?: () => void;
  aiQuota: { remaining: number; allowance: number; resetDate?: string } | null;
  customQuestionInput: string;
  onCustomQuestionChange: (val: string) => void;
  onRequestAiAnalysis: () => void;
  isAiLoading: boolean;
  aiInterpretation: string;
  aiError: string;
}

const THEME_ICONS: Record<LifeThemeId, React.ComponentType<{ className?: string }>> = {
  career_job: Briefcase,
  marriage_relationship: Heart,
  wealth_finance: Coins,
  house_property: Home,
  vehicle: Car,
  children: Baby,
  education: GraduationCap,
  health_vitality: Activity,
  business: TrendingUp,
  foreign_travel: Globe,
  litigation_competition: Scale,
  spirituality: Sparkles,
  family_parents: Users
};

export const AcharyaConsultationView: React.FC<AcharyaConsultationViewProps> = ({
  kundali,
  analysisResult,
  selectedThemeId,
  onSelectTheme,
  selectedQuestionId,
  onSelectQuestion,
  lang,
  chartStyle,
  setChartStyle,
  onOpenPrintReport,
  onEditKundali,
  aiQuota,
  customQuestionInput,
  onCustomQuestionChange,
  onRequestAiAnalysis,
  isAiLoading,
  aiInterpretation,
  aiError
}) => {
  const isHi = lang === 'hi';
  const allThemes = Object.values(LIFE_THEMES_REGISTRY);
  const currentTheme = LIFE_THEMES_REGISTRY[selectedThemeId] || allThemes[0];

  const clientName = kundali.birthDetails?.name || 'Native Client';
  const birthDate = kundali.birthDetails?.date || '1990-05-15';
  const birthPlace = kundali.birthDetails?.placeName || 'Vedic Horizon';
  const consultRef = `VJ-${(birthDate || '2026').replace(/[^0-9]/g, '')}-${(clientName || 'NATIVE').replace(/[^a-zA-Z0-9]/g, '').slice(0, 6).toUpperCase()}`;

  // Session Timer State
  const [sessionSeconds, setSessionSeconds] = useState<number>(0);
  const [isTimerRunning, setIsTimerRunning] = useState<boolean>(true);

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (isTimerRunning) {
      interval = setInterval(() => {
        setSessionSeconds((prev) => prev + 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isTimerRunning]);

  const formatTimer = (totalSeconds: number) => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Practitioner Case Notes Persistence
  const notesStorageKey = `astro_case_notes_${clientName.replace(/\s+/g, '_')}`;
  const [caseNotes, setCaseNotes] = useState<string>(() => {
    try {
      return localStorage.getItem(notesStorageKey) || '';
    } catch {
      return '';
    }
  });
  const [notesSaved, setNotesSaved] = useState<boolean>(false);

  const handleSaveNotes = () => {
    try {
      localStorage.setItem(notesStorageKey, caseNotes);
      setNotesSaved(true);
      setTimeout(() => setNotesSaved(false), 2500);
    } catch (e) {
      console.warn('Failed to save notes:', e);
    }
  };

  // Quick Inserter functions for practitioner convenience
  const handleInsertSnippet = (snippet: string) => {
    setCaseNotes((prev) => (prev ? `${prev}\n\n• ${snippet}` : `• ${snippet}`));
  };

  const { natalPromise, phalaBhutaWindows, lifeStageContext, themeConfig, question } = analysisResult;
  const primaryWindow = phalaBhutaWindows.primary;

  return (
    <div
      id="acharya-presentation-view"
      className="space-y-6 animate-fadeIn"
      role="tabpanel"
      aria-labelledby="role-tab-acharya"
    >
      {/* 1. Acharya Professional Consultation Header Bar (Compact Two-Row Layout) */}
      <div
        id="acharya-consultation-header"
        className="bg-astro-card border border-astro-border rounded-xl p-3 sm:px-4 sm:py-2.5 shadow-2xs space-y-2"
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          {/* Row 1 Left: Client & Case Details */}
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            <Award className="w-4 h-4 text-astro-accent shrink-0" />
            <h2 className="text-base sm:text-lg font-bold text-astro-primary font-serif truncate">
              {clientName}
            </h2>
            {onEditKundali && (
              <button
                id="btn-acharya-edit-in-profile-name"
                type="button"
                onClick={onEditKundali}
                className="p-1 rounded-md text-astro-accent hover:text-astro-primary hover:bg-[#FAF6ED] transition-colors cursor-pointer border border-astro-accent/30 shrink-0"
                title={isHi ? 'जन्म विवरण (DOB, समय, स्थान) बदलें एवं पुनर्परिकलन करें' : 'Edit Birth Details (DOB, Time, Place) & Recalculate Chart'}
                aria-label="Edit Birth Details"
              >
                <Edit3 className="w-3.5 h-3.5 text-astro-accent" />
              </button>
            )}
            <span className="text-[11px] font-mono font-bold bg-astro-teal-tint text-astro-primary px-2 py-0.5 rounded-md border border-astro-primary/20 shrink-0">
              {consultRef}
            </span>
          </div>

          {/* Row 1 Right: Session Timer & Action CTA */}
          <div className="flex flex-wrap items-center gap-2 shrink-0 self-start sm:self-auto">
            {/* Live Consultation Timer */}
            <div className="flex items-center gap-1.5 bg-astro-bg px-2.5 py-1 rounded-lg border border-astro-border shadow-2xs">
              <Clock className="w-3.5 h-3.5 text-astro-accent" />
              <span className="font-mono text-xs font-bold text-astro-text">
                {formatTimer(sessionSeconds)}
              </span>
              <div className="flex items-center gap-0.5 ml-1">
                <button
                  onClick={() => setIsTimerRunning(!isTimerRunning)}
                  className="p-0.5 text-astro-secondary-text hover:text-astro-text rounded cursor-pointer"
                  title={isTimerRunning ? 'Pause' : 'Start'}
                >
                  {isTimerRunning ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5 text-astro-primary" />}
                </button>
                <button
                  onClick={() => {
                    setIsTimerRunning(false);
                    setSessionSeconds(0);
                  }}
                  className="p-0.5 text-astro-secondary-text/60 hover:text-astro-secondary-text rounded cursor-pointer"
                  title="Reset Timer"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* Primary CTA: Deliver Consultation Report */}
            <button
              onClick={() => onOpenPrintReport('ACHARYA')}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-astro-primary hover:bg-astro-secondary text-white rounded-lg text-xs font-bold shadow-2xs transition-all touch-manipulation cursor-pointer"
            >
              <Printer className="w-3.5 h-3.5 text-astro-accent" />
              <span>{isHi ? 'रिपोर्ट प्रस्तुत करें' : 'Deliver Report'}</span>
            </button>
          </div>
        </div>

        {/* Row 2: Compact Metadata Horizontal Bar */}
        <div className="flex flex-wrap items-center gap-x-2.5 sm:gap-x-3 gap-y-1 text-xs text-astro-secondary-text pt-1.5 border-t border-astro-border/70">
          <span>{isHi ? 'जन्म:' : 'DOB:'} <strong className="text-astro-primary font-mono font-bold">{formatDOB(birthDate)}</strong></span>
          <span>•</span>
          <span>{isHi ? 'समय:' : 'Time:'} <strong className="text-astro-primary font-mono font-bold">{formatTimeToAMPM(kundali.birthDetails?.time)}</strong></span>
          <span>•</span>
          <span>{isHi ? 'आयु:' : 'Age:'} <strong className="text-astro-primary font-mono font-bold">{calculateAgeString(birthDate, lang) || `${lifeStageContext.years}y ${lifeStageContext.months}m`}</strong></span>
          <span>•</span>
          <span>{isHi ? 'स्थान:' : 'POB:'} <strong className="text-astro-text">{birthPlace}</strong></span>
          <span>•</span>
          <span className="text-astro-primary font-semibold">{isHi ? themeConfig.nameHi : themeConfig.nameEn}</span>
        </div>
      </div>

      {/* 2. 13-Theme Selector */}
      <div
        id="acharya-theme-selector"
        className="bg-astro-card border border-astro-border rounded-2xl p-4 sm:p-5 shadow-2xs space-y-3"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Compass className="w-4 h-4 text-astro-accent" />
            <h3 className="text-xs font-bold uppercase tracking-wider text-astro-text">
              {isHi ? '१३ शास्त्रीय जीवन क्षेत्र (परामर्श विषय)' : '13 Life Themes (Consultation Domain)'}
            </h3>
          </div>
          <span className="text-[11px] text-astro-secondary-text">
            {allThemes.length} {isHi ? 'विषय उपलब्ध' : 'Themes'}
          </span>
        </div>

        <div className="w-full overflow-x-auto custom-horizontal-scrollbar pb-2">
          <div className="flex items-center gap-2 min-w-max px-0.5">
            {allThemes.map((theme) => {
              const Icon = THEME_ICONS[theme.id] || Sparkles;
              const isSelected = selectedThemeId === theme.id;

              return (
                <button
                  key={theme.id}
                  onClick={() => onSelectTheme(theme.id)}
                  className={`flex items-center gap-2 px-3.5 py-2.5 rounded-xl text-xs font-semibold whitespace-nowrap border transition-all min-h-[44px] touch-manipulation cursor-pointer ${
                    isSelected
                      ? 'bg-astro-primary text-white border-astro-primary shadow-2xs ring-2 ring-astro-accent/30'
                      : 'bg-astro-bg border-astro-border text-astro-text hover:border-astro-accent'
                  }`}
                >
                  <Icon className={`w-4 h-4 ${isSelected ? 'text-astro-accent' : 'text-astro-secondary-text'}`} />
                  <span>{isHi ? theme.nameHi : theme.nameEn}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* 3. Question Focus Selector */}
      <div
        id="acharya-question-selector"
        className="bg-astro-card border border-astro-border rounded-2xl p-4 sm:p-5 shadow-2xs space-y-3"
      >
        <div className="flex items-center gap-2 border-b border-astro-border pb-2">
          <HelpCircle className="w-4 h-4 text-astro-accent" />
          <h3 className="text-xs font-bold uppercase tracking-wider text-astro-text">
            {isHi ? 'परामर्श inquiry' : 'Client Consultation Inquiry'}
          </h3>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
          {currentTheme.questions.map((q: LifeThemeQuestion) => {
            const isSelected = selectedQuestionId === q.id;

            return (
              <button
                key={q.id}
                onClick={() => onSelectQuestion(q.id)}
                className={`p-3 rounded-xl border text-left text-xs transition-all min-h-[48px] flex items-start gap-2.5 touch-manipulation cursor-pointer ${
                  isSelected
                    ? 'bg-astro-bg border-astro-primary shadow-2xs ring-1 ring-astro-primary font-bold text-astro-primary'
                    : 'bg-astro-bg border-astro-border text-astro-text hover:border-astro-accent'
                }`}
              >
                <span className={`w-2 h-2 rounded-full mt-1 shrink-0 ${isSelected ? 'bg-astro-primary' : 'bg-astro-border'}`} />
                <span className="leading-snug">{isHi ? q.questionHi : q.questionEn}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* 4. Split Consultation Workspace (Desktop >= 1200px: 2 columns) */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-start">
        {/* ============================================================ */}
        {/* LEFT COLUMN: DETERMINISTIC ASTROLOGICAL EVIDENCE (7 Cols)    */}
        {/* ============================================================ */}
        <div className="xl:col-span-7 space-y-6">
          {/* Natal Promise Verdict Card */}
          <NatalPromiseVerdictCard
            analysisResult={analysisResult}
            lang={lang}
          />

          {/* Synchronized Chart Rail */}
          <AdvancedChartRail
            kundali={kundali}
            relevantVargas={analysisResult.relevantVargas}
            lang={lang}
            chartStyle={chartStyle}
            onStyleChange={setChartStyle}
          />

          {/* Dasha + Gochar Convergence Matrix */}
          <DashaGocharConvergenceCard
            analysisResult={analysisResult}
            lang={lang}
          />

          {/* Phala-Bhuta Timing Windows */}
          <PhalaBhutaTimeline
            analysisResult={analysisResult}
            lang={lang}
          />

          {/* Categorical Astrological Evidence Grid */}
          <AstrologicalEvidenceGrid
            analysisResult={analysisResult}
            lang={lang}
          />
        </div>

        {/* ============================================================ */}
        {/* RIGHT COLUMN: PRACTITIONER CONSULTATION CONSOLE (5 Cols)     */}
        {/* ============================================================ */}
        <div className="xl:col-span-5 space-y-6 xl:sticky xl:top-6">
          {/* A. Practitioner Live Case Notes */}
          <div
            id="practitioner-case-notes"
            className="bg-astro-card border border-astro-border rounded-2xl p-5 shadow-2xs space-y-3.5"
          >
            <div className="flex items-center justify-between border-b border-astro-border pb-2.5">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-astro-primary" />
                <h3 className="text-xs font-bold uppercase tracking-wider text-astro-text">
                  {isHi ? 'परामर्शदाता प्रकरण अभिलेख (केस नोट्स)' : 'Practitioner Case Notes & Counsel'}
                </h3>
              </div>
              <button
                onClick={handleSaveNotes}
                className="flex items-center gap-1 px-2.5 py-1 bg-astro-primary hover:bg-astro-secondary text-white rounded-lg text-xs font-bold transition-all shadow-2xs cursor-pointer"
              >
                {notesSaved ? <Check className="w-3.5 h-3.5 text-emerald-300" /> : <Save className="w-3.5 h-3.5" />}
                <span>{notesSaved ? (isHi ? 'सहेजा गया!' : 'Saved!') : (isHi ? 'सहेजें' : 'Save Notes')}</span>
              </button>
            </div>

            {/* Quick Inserter Helpers */}
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[11px] text-astro-secondary-text">
                {isHi ? 'त्वरित प्रविष्टि:' : 'Quick Insert:'}
              </span>
              <button
                onClick={() => handleInsertSnippet(`Natal Promise: ${isHi ? natalPromise.verdictHi : natalPromise.verdictEn}`)}
                className="px-2 py-0.5 text-[10px] font-semibold rounded bg-astro-bg text-astro-primary border border-astro-border hover:bg-astro-teal-tint cursor-pointer"
              >
                + {isHi ? 'जन्मजात फल' : 'Promise'}
              </button>
              <button
                onClick={() => handleInsertSnippet(`Primary Timing: ${primaryWindow.startDate} to ${primaryWindow.endDate}`)}
                className="px-2 py-0.5 text-[10px] font-semibold rounded bg-astro-bg text-astro-primary border border-astro-border hover:bg-astro-teal-tint cursor-pointer"
              >
                + {isHi ? 'मुख्य समय' : 'Timing'}
              </button>
              <button
                onClick={() => handleInsertSnippet(`Active Dasha: ${primaryWindow.mahadashaLord}–${primaryWindow.antardashaLord}`)}
                className="px-2 py-0.5 text-[10px] font-semibold rounded bg-astro-bg text-astro-primary border border-astro-border hover:bg-astro-teal-tint cursor-pointer"
              >
                + {isHi ? 'सक्रिय दशा' : 'Dasha'}
              </button>
            </div>

            {/* Live Case Notes Textarea */}
            <textarea
              value={caseNotes}
              onChange={(e) => setCaseNotes(e.target.value)}
              placeholder={
                isHi
                  ? 'परामर्श के दौरान जातक के विशिष्ट प्रश्न, पारिवारिक संदर्भ, निर्णय, उपाय व फलादेश सुझाव यहाँ लिखें...'
                  : 'Record client inquiries, specific context, dilemmas, custom remedies, and consultation takeaways here...'
              }
              rows={8}
              className="w-full p-3.5 rounded-xl border border-astro-border bg-astro-bg text-xs text-astro-text placeholder-[#4B5956]/60 focus:outline-none focus:ring-2 focus:ring-astro-primary/30 font-sans leading-relaxed resize-y"
            />
          </div>

          {/* B. Prescribed Remedies & Guidance Summary */}
          <RemediesGuidanceCard
            analysisResult={analysisResult}
            lang={lang}
          />

          {/* C. Acharya Sanctuary (AI Assisted Contextual Synthesis) */}
          <AcharyaSanctuaryContainer
            lang={lang}
            aiQuota={aiQuota}
            customQuestionInput={customQuestionInput}
            onCustomQuestionChange={onCustomQuestionChange}
            onRequestAiAnalysis={onRequestAiAnalysis}
            isAiLoading={isAiLoading}
            aiInterpretation={aiInterpretation}
            aiError={aiError}
          />
        </div>
      </div>
    </div>
  );
};
